import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Card, Form, Input, Modal, Select, Space, Table, Typography, message } from 'antd'
import Popconfirm from '../components/Popconfirm'
import * as api from '../lib/api'
import { useI18n } from '../i18n/I18nProvider'
import Title from 'antd/es/skeleton/Title'

const { Text } = Typography

function staffLabel(user) {
  return [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email
}

export default function DepartmentManager({ embedded = false, sessionId, canManage = true }) {
  const { t } = useI18n()
  const [departments, setDepartments] = useState([])
  const [loading, setLoading] = useState(true)
  const [businessUnits, setBusinessUnits] = useState([])
  const [headOptions, setHeadOptions] = useState([])
  const [editingDepartment, setEditingDepartment] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm()
  const isSessionScoped = Boolean(sessionId)

  const loadDepartments = useCallback(async () => {
    setLoading(true)
    try {
      setDepartments(isSessionScoped ? await api.getSessionDepartments(sessionId) : await api.getDepartments())
    } catch (e) {
      message.error(e.message || t('organization.messages.departments_load_failed', 'Unable to load departments'))
    } finally {
      setLoading(false)
    }
  }, [isSessionScoped, sessionId, t])

  useEffect(() => {
    loadDepartments()
  }, [loadDepartments])

  useEffect(() => {
    if (!isSessionScoped) return
    api.getBusinessUnits().then(setBusinessUnits).catch(() => setBusinessUnits([]))
  }, [isSessionScoped])

  const loadHeads = useCallback(async (q) => {
    try {
      const users = await api.searchStaff({ q, role: 'department_head' })
      setHeadOptions(users.map(user => ({ value: user.id, label: staffLabel(user) })))
    } catch (_) {
      setHeadOptions([])
    }
  }, [])

  async function deleteDepartment(department) {
    try {
      if (isSessionScoped) await api.deleteSessionDepartment(sessionId, department.id)
      else await api.deleteDepartment(department.id)
      message.success(t('organization.messages.department_deleted', 'Department deleted'))
      await loadDepartments()
    } catch (e) {
      message.error(e.message || t('organization.messages.department_delete_failed', 'Unable to delete department'))
    }
  }

  function openCreateModal() {
    setEditingDepartment(null)
    form.setFieldsValue({ name: '', code: '', business_unit: null, head: null })
    setHeadOptions([])
    setModalOpen(true)
    loadHeads()
  }

  function openEditModal(department) {
    setEditingDepartment(department)
    form.setFieldsValue({
      name: department.name,
      code: department.code || '',
      business_unit: department.business_unit || null,
      head: department.head || null
    })
    setHeadOptions(department.head
      ? [{
        value: department.head,
        label: [department.head_first_name, department.head_last_name].filter(Boolean).join(' ') || department.head_email
      }]
      : [])
    setModalOpen(true)
  }

  async function saveSessionDepartment() {
    const values = await form.validateFields()
    setSaving(true)
    try {
      if (editingDepartment) {
        await api.updateSessionDepartment(sessionId, editingDepartment.id, values)
        message.success(t('organization.messages.department_saved', 'Department saved'))
      } else {
        await api.createSessionDepartment(sessionId, values)
        message.success(t('organization.messages.department_created', 'Department created'))
      }
      setModalOpen(false)
      setEditingDepartment(null)
      await loadDepartments()
    } catch (e) {
      message.error(e.message || t('organization.messages.department_save_failed', 'Unable to save department'))
    } finally {
      setSaving(false)
    }
  }

  const columns = [
    { title: t('organization.table.department', 'Department'), dataIndex: 'name', key: 'name' },
    { title: 'Department Code', dataIndex: 'code', key: 'code', render: value => value || '—' },
    !isSessionScoped && {
      title: t('organization.table.organization', 'Organization'),
      dataIndex: 'organization_name',
      key: 'organization_name'
    },
    !isSessionScoped && {
      title: t('organization.table.region', 'Region'),
      dataIndex: 'region_name',
      key: 'region_name'
    },
    isSessionScoped && {
      title: t('organization.table.business_unit', 'Business Unit'),
      dataIndex: 'business_unit_name',
      key: 'business_unit_name',
      render: (value, record) => record.region_uses_business_units === false
        ? <Text type='secondary'>{t('organization.not_used', 'Not used')}</Text>
        : value || <Text type='secondary'>{t('organization.not_assigned', 'Not assigned')}</Text>
    },
    isSessionScoped && {
      title: t('organization.table.department_head', 'Department Head'),
      key: 'head',
      render: (_, record) => record.head_email
        ? (
          <div>
            <div>{[record.head_first_name, record.head_last_name].filter(Boolean).join(' ') || record.head_email}</div>
            <Text type='secondary' style={{ fontSize: 12 }}>{record.head_email}</Text>
          </div>
        )
        : <Text type='secondary'>{t('organization.not_assigned', 'Not assigned')}</Text>
    },
    isSessionScoped && {
      title: t('organization.table.bu_head', 'BU Head'),
      key: 'bu_head',
      render: (_, record) => record.bu_head
        ? (
          <div>
            <div>{[record.bu_head.first_name, record.bu_head.last_name].filter(Boolean).join(' ') || record.bu_head.email}</div>
            <Text type='secondary' style={{ fontSize: 12 }}>{record.bu_head.email}</Text>
          </div>
        )
        : <Text type='secondary'>{t('organization.not_assigned', 'Not assigned')}</Text>
    },
    canManage && {
      title: '',
      key: 'actions',
      width: 170,
      render: (_, record) => (
        <Space size='small'>
          {isSessionScoped
            ? <Button size='small' onClick={() => openEditModal(record)}>{t('common.edit', 'Edit')}</Button>
            : <Link to={`/departments/${record.id}/edit`}><Button size='small'>{t('common.edit', 'Edit')}</Button></Link>}
          <Popconfirm title={t('organization.confirm.delete_department', 'Delete department?')} okButtonProps={{ danger: true, className: 'tti-btn-danger' }} onConfirm={() => deleteDepartment(record)}>
            <Button size='small' className='tti-btn-danger'>{t('common.delete', 'Delete')}</Button>
          </Popconfirm>
        </Space>
      )
    }
  ].filter(Boolean)

  const table = (
    <Card
      size='small'
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>{t('organization.departments.title', 'Departments')}</Title>
        {canManage
          ? (isSessionScoped
            ? <Button size='small' className='tti-btn-primary-grey' onClick={openCreateModal}>{t('organization.actions.create_department', 'Create Department')}</Button>
            : <Link to='/departments/new'><Button size='small' className='tti-btn-primary-grey'>{t('organization.actions.create_department', 'Create Department')}</Button></Link>)
          : null}
      </div>
      <Table
        dataSource={departments}
        columns={columns}
        rowKey='id'
        size='small'
        loading={loading}
        pagination={false}
      />
      {isSessionScoped && (
        <Modal
          title={editingDepartment
            ? t('organization.actions.edit_department', 'Edit Department')
            : t('organization.actions.create_department', 'Create Department')}
          open={modalOpen}
          onOk={saveSessionDepartment}
          onCancel={() => {
            setModalOpen(false)
            setEditingDepartment(null)
          }}
          okButtonProps={{ loading: saving }}
        >
          <Form form={form} layout='vertical'>
            <Form.Item name='name' label={t('organization.fields.department_name', 'Department Name')} rules={[{ required: true, message: t('organization.validation.department_name_required', 'Department name is required') }]}>
              <Input placeholder={t('organization.examples.department_name', 'Finance')} />
            </Form.Item>
            <Form.Item name='code' label='Department Code (optional)'>
              <Input maxLength={80} placeholder='Enter the assigned department code' />
            </Form.Item>
            <Form.Item name='business_unit' label={t('organization.fields.business_unit', 'Business Unit')}>
              <Select
                allowClear
                showSearch
                optionFilterProp='label'
                options={businessUnits.map(unit => ({ value: unit.id, label: unit.name }))}
                placeholder={t('organization.fields.select_business_unit', 'Select business unit')}
              />
            </Form.Item>
            <Form.Item name='head' label={t('organization.fields.department_head', 'Department Head')}>
              <Select
                allowClear
                showSearch
                filterOption={false}
                onSearch={loadHeads}
                options={headOptions}
                placeholder={t('organization.fields.search_department_head', 'Search department head')}
              />
            </Form.Item>
          </Form>
        </Modal>
      )}
    </Card>
  )

  if (embedded) return table

  return <div style={{ maxWidth: 1100, margin: '0 auto', padding: 20 }}>{table}</div>
}
